/**
 * Live wind-comfort screening orchestration.
 *
 * Bridges the gap left by the SDK (which has no height-map getter) for the rapid
 * wind model: it pulls terrain and building triangle meshes from the live Forma
 * geometry surface, rasterizes them into normalized height maps, and runs the
 * rapid wind-comfort prediction. The result is the raw comfort grid, which the
 * caller normalizes into `wind-comfort` evidence via `analyzeWindComfortScreening`.
 *
 * Geometry fetches degrade gracefully (a failing or empty mesh is skipped) and
 * any error is captured as a failure string instead of thrown, so a partial
 * model still lets the rest of the pipeline complete.
 */

import {
  buildWindHeightMaps,
  predictRapidWindComfort,
  type FormaGeometryDetailClient,
  type FormaPredictiveAnalysisGroundGrid,
  type FormaWindPredictionClient,
  type WindComfortScale,
} from './index';

/**
 * Forma's documented default comfort scale (used in the SDK's rapid-wind
 * example). Surfaced as a constant so callers get a sensible default.
 */
export const DEFAULT_WIND_COMFORT_SCALE: WindComfortScale = 'lawson_lddc';

export interface CollectWindComfortScreeningOptions {
  /** Live geometry surface used to read triangle meshes. */
  geometry: FormaGeometryDetailClient;
  /** Wind prediction surface (wind rose + rapid prediction). */
  prediction: FormaWindPredictionClient;
  /** Building element paths whose meshes drive the building height map. */
  buildingPaths: readonly string[];
  /** Optional terrain element paths; omitted terrain is treated as flat ground. */
  terrainPaths?: readonly string[];
  /** Optional root URN scoping the geometry reads. */
  rootUrn?: string;
  /** World-space center the height-map grid is built around. */
  center: { x: number; y: number };
  /** Comfort scale for the prediction. Defaults to {@link DEFAULT_WIND_COMFORT_SCALE}. */
  comfortScale?: WindComfortScale;
  /** Grid edge length in cells. Defaults to the rasterizer default (500). */
  size?: number;
  /** Physical length of one cell in meters. Defaults to the rasterizer default (1.5). */
  cellSize?: number;
}

export interface WindComfortScreeningResult {
  /** Rapid wind-comfort grid when the prediction succeeded. */
  grid?: FormaPredictiveAnalysisGroundGrid;
  /** Comfort scale the prediction used (resolved default included). */
  comfortScale: WindComfortScale;
  /** Reason the screening could not produce a grid, when applicable. */
  failure?: string;
}

/**
 * Rasterizes the live model geometry into wind height maps and runs the rapid
 * wind-comfort prediction. Returns the comfort grid, or a captured failure when
 * no geometry is available or the prediction throws. Terrain is optional; when
 * omitted the rasterizer assumes flat ground beneath the buildings.
 */
export async function collectWindComfortScreening(
  options: CollectWindComfortScreeningOptions,
): Promise<WindComfortScreeningResult> {
  const comfortScale = options.comfortScale ?? DEFAULT_WIND_COMFORT_SCALE;

  try {
    const [terrainTriangles, buildingTriangles] = await Promise.all([
      fetchTriangles(options.geometry, options.terrainPaths ?? [], options.rootUrn),
      fetchTriangles(options.geometry, options.buildingPaths, options.rootUrn),
    ]);

    if (terrainTriangles.length === 0 && buildingTriangles.length === 0) {
      return { comfortScale, failure: 'No geometry triangles available for wind height maps.' };
    }

    const heightMaps = buildWindHeightMaps({
      terrainTriangles,
      buildingTriangles,
      center: options.center,
      ...(options.size === undefined ? {} : { size: options.size }),
      ...(options.cellSize === undefined ? {} : { cellSize: options.cellSize }),
    });
    const grid = await predictRapidWindComfort({
      forma: options.prediction,
      heightMaps,
      comfortScale,
    });

    return { grid, comfortScale };
  } catch (error) {
    return { comfortScale, failure: error instanceof Error ? error.message : String(error) };
  }
}

async function fetchTriangles(
  geometry: FormaGeometryDetailClient,
  paths: readonly string[],
  rootUrn: string | undefined,
): Promise<Float32Array[]> {
  const meshes = await Promise.all(paths.map((path) => safeGetTriangles(geometry, path, rootUrn)));

  return meshes.filter((mesh): mesh is Float32Array => mesh !== undefined);
}

async function safeGetTriangles(
  geometry: FormaGeometryDetailClient,
  path: string,
  rootUrn: string | undefined,
): Promise<Float32Array | undefined> {
  try {
    const request = rootUrn === undefined ? { path } : { path, urn: rootUrn };
    const triangles = await geometry.getTriangles(request);

    return triangles !== undefined && triangles.length >= 9 ? triangles : undefined;
  } catch {
    return undefined;
  }
}
